import {Flex, Tooltip} from 'antd';
import {ModelDTO} from 'hemwb-api';
import {FileOutlined, HeartFilled, HeartOutlined} from '@ant-design/icons';
import {GlobalAdaptationCell} from '../list/GlobalAdaptationCell';
import {ModelStatusTag, tagType} from '../list/ModelStatusTag';
import {TO_LOCALE_DATE_STRING_OPTIONS} from '../../../core/TableWithSortAndPagination';
import {useToggleFavorite} from '../../../../store/favorite';

/**
 * Columns for the Overview tab family tree table
 * Subset of the main ModelList columns without sorting
 */
export const useGetOverviewColumns = (isExternalUser: boolean) => {
  const toggleFavorite = useToggleFavorite();

  const favoriteColumn = {
    title: '',
    key: 'favorite',
    width: '3%',
    align: 'center' as const,
    render: (model: ModelDTO) => {
      const isFav = !!model.favorite;
      return (
        <Tooltip title={isFav ? 'Remove from favorites' : 'Add to favorites'}>
          <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1}}>
            {isFav ? (
              <HeartFilled
                style={{color: '#00857c', cursor: 'pointer', fontSize: '16px'}}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(model.uuid, true);
                }}
              />
            ) : (
              <HeartOutlined
                style={{color: '#bfbfbf', cursor: 'pointer', fontSize: '16px'}}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(model.uuid, false);
                }}
              />
            )}
          </span>
        </Tooltip>
      );
    },
  };

  return [
    ...(isExternalUser ? [] : [favoriteColumn]),
    {
      title: 'Model Name',
      key: 'name',
      width: '35%',
      ellipsis: {
        showTitle: true,
      },
      render: (model: ModelDTO & {isChild?: boolean; children?: ModelDTO[]; level?: number}) => {
        const hasChildren = !!model?.children?.length;
        const level = (model as any).level || 0;

        if (model?.isChild) {
          // Child rows - progressive indent based on level
          // If has children: no extra indent (expand icon provides spacing)
          // If leaf: indent to align after where arrow would be (level * 48 + 16)
          const indentAmount = hasChildren ? 0 : level * 48 + 16;

          if (model.metadata.global !== 'true') {
            return (
              <Flex style={{marginLeft: `${indentAmount}px`, minWidth: 0}} title={model?.name}>
                <FileOutlined style={{marginRight: '16px', flexShrink: 0}} />
                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0}}>
                  {model?.name}
                </span>
              </Flex>
            );
          }
          return (
            <Flex style={{marginLeft: `${indentAmount}px`, minWidth: 0}} title={model?.name}>
              <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0}}>
                {model?.name}
              </span>
            </Flex>
          );
        }

        // Root/parent rows - no indent
        return (
          <Flex style={{minWidth: 0}} title={model?.name}>
            <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0}}>
              {model?.name}
            </span>
          </Flex>
        );
      },
    },
    {
      title: 'Global / Adaptation',
      key: 'globalAdaptation',
      width: '18%',
      render: (model: ModelDTO) => <GlobalAdaptationCell model={model} />,
    },
    {
      title: 'Latest Version',
      key: 'latestVersion',
      width: '13%',
      render: (model: ModelDTO) =>
        model.metadata.version_release_of_model ? <span>Ver. {model.metadata.version_release_of_model}</span> : '-',
    },
    {
      title: 'Latest modified',
      key: 'modifiedOn',
      width: '12%',
      render: (model: ModelDTO & {modifiedOn: string}) =>
        model.modifiedOn ? new Date(model.modifiedOn).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: '13%',
      render: (model: ModelDTO) => <ModelStatusTag status={model?.metadata?.status?.toLowerCase() as tagType} />,
    },
    {
      title: 'HEDS Lead',
      key: 'steward',
      width: '17%',
      render: (model: ModelDTO) =>
        model.metadata.name_of_model_steward ? <span>{model.metadata.name_of_model_steward}</span> : '-',
    },
  ];
};
